export type LanguageOption = {
  code: string;
  name: string;
  nativeName?: string;
  googleCode?: string;
  flag?: string;
  aliases?: string[];
};

export const DEFAULT_LANGUAGE_FLAG = '🌐';

const BASE_LANGUAGES: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', aliases: ['american', 'usa'] },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', aliases: ['espanol', 'español', 'spain', 'latin america', 'latin american spanish'] },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹', aliases: ['portugues', 'português'] },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'zh', googleCode: 'zh-CN', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', aliases: ['mandarin', 'simplified chinese', 'chinese simplified'] },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: '🇧🇩' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', flag: '🇲🇾' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk', flag: '🇳🇴' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', flag: '🇫🇮' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'he', googleCode: 'iw', name: 'Hebrew', nativeName: 'עברית', flag: '🇮🇱' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', flag: '🇭🇺' },
];

const EXTENDED_GOOGLE_LANGUAGES: LanguageOption[] = [
  { code: 'ab', name: 'Abkhaz' },
  { code: 'ace', name: 'Acehnese' },
  { code: 'ach', name: 'Acholi' },
  { code: 'af', name: 'Afrikaans' },
  { code: 'sq', name: 'Albanian' },
  { code: 'alz', name: 'Alur' },
  { code: 'am', name: 'Amharic' },
  { code: 'as', name: 'Assamese' },
  { code: 'awa', name: 'Awadhi' },
  { code: 'ay', name: 'Aymara' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'ban', name: 'Balinese' },
  { code: 'bm', name: 'Bambara' },
  { code: 'ba', name: 'Bashkir' },
  { code: 'eu', name: 'Basque' },
  { code: 'btx', name: 'Batak Karo' },
  { code: 'bts', name: 'Batak Simalungun' },
  { code: 'bbc', name: 'Batak Toba' },
  { code: 'be', name: 'Belarusian' },
  { code: 'bem', name: 'Bemba' },
  { code: 'bew', name: 'Betawi' },
  { code: 'bho', name: 'Bhojpuri' },
  { code: 'bik', name: 'Bikol' },
  { code: 'bs', name: 'Bosnian' },
  { code: 'br', name: 'Breton' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'bua', name: 'Buryat' },
  { code: 'yue', name: 'Cantonese' },
  { code: 'ca', name: 'Catalan' },
  { code: 'ceb', name: 'Cebuano' },
  { code: 'ny', name: 'Chichewa (Nyanja)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', aliases: ['traditional chinese'] },
  { code: 'cv', name: 'Chuvash' },
  { code: 'co', name: 'Corsican' },
  { code: 'crh', name: 'Crimean Tatar' },
  { code: 'hr', name: 'Croatian' },
  { code: 'din', name: 'Dinka' },
  { code: 'dv', name: 'Divehi' },
  { code: 'doi', name: 'Dogri' },
  { code: 'dov', name: 'Dombe' },
  { code: 'dz', name: 'Dzongkha' },
  { code: 'eo', name: 'Esperanto' },
  { code: 'et', name: 'Estonian' },
  { code: 'ee', name: 'Ewe' },
  { code: 'fj', name: 'Fijian' },
  { code: 'fil', googleCode: 'tl', name: 'Filipino (Tagalog)', aliases: ['tagalog', 'tl'] },
  { code: 'fr-CA', googleCode: 'fr', name: 'French (Canada)', aliases: ['canadian french'] },
  { code: 'fy', name: 'Frisian' },
  { code: 'ff', name: 'Fulfulde' },
  { code: 'gaa', name: 'Ga' },
  { code: 'lg', name: 'Ganda (Luganda)', aliases: ['luganda'] },
  { code: 'ka', name: 'Georgian' },
  { code: 'gn', name: 'Guarani' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'ht', name: 'Haitian Creole' },
  { code: 'cnh', name: 'Hakha Chin' },
  { code: 'ha', name: 'Hausa' },
  { code: 'haw', name: 'Hawaiian' },
  { code: 'hil', name: 'Hiligaynon' },
  { code: 'hmn', name: 'Hmong' },
  { code: 'hrx', name: 'Hunsrik' },
  { code: 'is', name: 'Icelandic' },
  { code: 'ig', name: 'Igbo' },
  { code: 'ilo', name: 'Iloko' },
  { code: 'ga', name: 'Irish' },
  { code: 'jv', googleCode: 'jw', name: 'Javanese', aliases: ['jw'] },
  { code: 'kn', name: 'Kannada' },
  { code: 'pam', name: 'Kapampangan' },
  { code: 'kk', name: 'Kazakh' },
  { code: 'km', name: 'Khmer' },
  { code: 'cgg', name: 'Kiga' },
  { code: 'rw', name: 'Kinyarwanda' },
  { code: 'ktu', name: 'Kituba' },
  { code: 'gom', name: 'Konkani' },
  { code: 'kri', name: 'Krio' },
  { code: 'ku', name: 'Kurdish (Kurmanji)' },
  { code: 'ckb', name: 'Kurdish (Sorani)' },
  { code: 'ky', name: 'Kyrgyz' },
  { code: 'lo', name: 'Lao' },
  { code: 'ltg', name: 'Latgalian' },
  { code: 'la', name: 'Latin' },
  { code: 'lv', name: 'Latvian' },
  { code: 'lij', name: 'Ligurian' },
  { code: 'li', name: 'Limburgan' },
  { code: 'ln', name: 'Lingala' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'lmo', name: 'Lombard' },
  { code: 'luo', name: 'Luo' },
  { code: 'lb', name: 'Luxembourgish' },
  { code: 'mk', name: 'Macedonian' },
  { code: 'mai', name: 'Maithili' },
  { code: 'mak', name: 'Makassar' },
  { code: 'mg', name: 'Malagasy' },
  { code: 'ms-Arab', googleCode: 'ms', name: 'Malay (Jawi)' },
  { code: 'mt', name: 'Maltese' },
  { code: 'mi', name: 'Maori' },
  { code: 'mr', name: 'Marathi' },
  { code: 'chm', name: 'Meadow Mari' },
  { code: 'mni-Mtei', name: 'Meiteilon (Manipuri)' },
  { code: 'min', name: 'Minang' },
  { code: 'lus', name: 'Mizo' },
  { code: 'mn', name: 'Mongolian' },
  { code: 'my', name: 'Myanmar (Burmese)' },
  { code: 'nr', name: 'Ndebele (South)' },
  { code: 'new', name: 'Nepalbhasa (Newari)' },
  { code: 'ne', name: 'Nepali' },
  { code: 'nso', name: 'Northern Sotho (Sepedi)' },
  { code: 'nus', name: 'Nuer' },
  { code: 'oc', name: 'Occitan' },
  { code: 'or', name: 'Odia (Oriya)' },
  { code: 'om', name: 'Oromo' },
  { code: 'pag', name: 'Pangasinan' },
  { code: 'pap', name: 'Papiamento' },
  { code: 'ps', name: 'Pashto' },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی' },
  { code: 'pt-BR', googleCode: 'pt', name: 'Portuguese (Brazil)', aliases: ['brazilian portuguese'] },
  { code: 'pt-PT', googleCode: 'pt', name: 'Portuguese (Portugal)', aliases: ['portugal portuguese'] },
  { code: 'pa-Arab', googleCode: 'pa', name: 'Punjabi (Shahmukhi)' },
  { code: 'qu', name: 'Quechua' },
  { code: 'rom', name: 'Romani' },
  { code: 'rn', name: 'Rundi' },
  { code: 'sm', name: 'Samoan' },
  { code: 'sg', name: 'Sango' },
  { code: 'sa', name: 'Sanskrit' },
  { code: 'gd', name: 'Scots Gaelic' },
  { code: 'sr', name: 'Serbian' },
  { code: 'st', name: 'Sesotho' },
  { code: 'crs', name: 'Seychellois Creole' },
  { code: 'shn', name: 'Shan' },
  { code: 'sn', name: 'Shona' },
  { code: 'scn', name: 'Sicilian' },
  { code: 'szl', name: 'Silesian' },
  { code: 'sd', name: 'Sindhi' },
  { code: 'si', name: 'Sinhala (Sinhalese)' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'so', name: 'Somali' },
  { code: 'es-419', googleCode: 'es', name: 'Spanish (Latin America)', aliases: ['latin america spanish', 'latam spanish'] },
  { code: 'es-ES', googleCode: 'es', name: 'Spanish (Spain)', aliases: ['spain spanish', 'castilian'] },
  { code: 'es-MX', googleCode: 'es', name: 'Spanish (Mexico)' },
  { code: 'es-US', googleCode: 'es', name: 'Spanish (United States)' },
  { code: 'su', name: 'Sundanese' },
  { code: 'sw', name: 'Swahili' },
  { code: 'ss', name: 'Swati' },
  { code: 'tg', name: 'Tajik' },
  { code: 'ta', name: 'Tamil' },
  { code: 'tt', name: 'Tatar' },
  { code: 'te', name: 'Telugu' },
  { code: 'tet', name: 'Tetum' },
  { code: 'ti', name: 'Tigrinya' },
  { code: 'ts', name: 'Tsonga' },
  { code: 'tn', name: 'Tswana' },
  { code: 'tk', name: 'Turkmen' },
  { code: 'ak', name: 'Twi (Akan)' },
  { code: 'ur', name: 'Urdu' },
  { code: 'ug', name: 'Uyghur' },
  { code: 'cy', name: 'Welsh' },
  { code: 'xh', name: 'Xhosa' },
  { code: 'yi', name: 'Yiddish' },
  { code: 'yo', name: 'Yoruba' },
  { code: 'yua', name: 'Yucatec Maya' },
  { code: 'zu', name: 'Zulu' },
];

export const LANGUAGES: LanguageOption[] = [
  ...BASE_LANGUAGES,
  ...EXTENDED_GOOGLE_LANGUAGES,
];

export const GOOGLE_TRANSLATE_LANGUAGE_CODES = [
  'af', 'sq', 'ar', 'hy', 'az', 'eu', 'be', 'bn', 'bs', 'bg',
  'ca', 'ceb', 'ny', 'zh-CN', 'zh-TW', 'co', 'hr', 'cs', 'da', 'nl',
  'en', 'eo', 'et', 'tl', 'fi', 'fr', 'fy', 'gl', 'ka', 'de',
  'el', 'gu', 'ht', 'ha', 'haw', 'iw', 'hi', 'hmn', 'hu', 'is',
  'ig', 'id', 'ga', 'it', 'ja', 'jw', 'kn', 'kk', 'km', 'ko',
  'ku', 'ky', 'lo', 'la', 'lv', 'lt', 'lb', 'mk', 'mg', 'ms',
  'ml', 'mt', 'mi', 'mr', 'mn', 'my', 'ne', 'no', 'ps', 'fa',
  'pl', 'pt', 'pa', 'ro', 'ru', 'sm', 'gd', 'sr', 'st', 'sn',
  'sd', 'si', 'sk', 'sl', 'so', 'es', 'su', 'sw', 'sv', 'tg',
  'ta', 'te', 'th', 'tr', 'uk', 'ur', 'uz', 'vi', 'cy', 'xh',
  'yi', 'yo', 'zu',
];

export const getLanguageLabel = (language: LanguageOption): string => {
  if (!language.nativeName || language.nativeName === language.name) {
    return language.name;
  }

  return `${language.name} (${language.nativeName})`;
};
